import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import {
  DialogHeader,
  dialogActionsSx,
  dialogPaperProps,
} from "../../components/DialogParts";
import {
  outlinedButtonSx,
  primaryButtonSx,
} from "../../../Organization/CodeSecurity/codeSecurityShared";
import VendorLogoField from "../../components/VendorLogoField";

// Add / edit dialog for a Trust Center list item. `fields` describes the form:
// { name, label, type: "text" | "textarea" | "select" | "multiselect" |
//   "tags" | "suggest" | "date" | "file", options, minRows, max,
//   required, helperText, showIf(values) }.
// `validate(values)` may add errors the field list cannot express.
// Remove lives here (not on every row), like Archive Risk on the detail page.
const ItemDialog = ({
  open,
  title,
  fields,
  initialValues,
  emptyValues,
  onSave,
  onRemove,
  onClose,
  removeHelperText,
  // Set when this item cannot be removed yet: shown instead of the button.
  removeBlockedReason,
  validate,
}) => {
  const theme = useTheme();
  const [values, setValues] = useState(emptyValues);
  const [errors, setErrors] = useState({});
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [uploading, setUploading] = useState(false);
  const isEdit = !!initialValues?.id;

  useEffect(() => {
    if (open) {
      setValues(initialValues || emptyValues);
      setErrors({});
      setConfirmRemove(false);
      setUploading(false);
    }
  }, [open, initialValues, emptyValues]);

  const visibleFields = fields.filter((f) => !f.showIf || f.showIf(values));

  const setValue = (name, value) =>
    setValues((prev) => ({ ...prev, [name]: value }));

  const handleSave = () => {
    const nextErrors = {};
    visibleFields.forEach((field) => {
      const value = values[field.name];
      if (
        field.required &&
        (value === undefined || value === null || String(value).trim() === "")
      ) {
        nextErrors[field.name] = `${field.label} is required.`;
      }
    });
    if (validate) Object.assign(nextErrors, validate(values), nextErrors);
    setErrors(nextErrors);
    if (Object.keys(nextErrors).length > 0) return;
    onSave(values);
    onClose();
  };

  const renderField = (field) => {
    if (field.type === "logo") {
      return (
        <VendorLogoField
          key={field.name}
          label={field.label}
          hint={field.helperText}
          logoPath={values[field.name] || ""}
          error={errors[field.name]}
          upload={field.upload}
          onChange={(path) => setValue(field.name, path)}
          onUploading={setUploading}
        />
      );
    }

    if (field.type === "file") {
      return (
        <Box key={field.name}>
          <Button
            component="label"
            variant="outlined"
            startIcon={<UploadFileOutlinedIcon />}
            sx={{ borderRadius: "8px", textTransform: "none" }}
          >
            {values[field.name] ? "Replace file" : field.label}
            <input
              hidden
              type="file"
              accept=".pdf,.docx,.xlsx"
              onChange={(e) =>
                e.target.files?.[0] &&
                setValue(field.name, e.target.files[0].name)
              }
            />
          </Button>
          <Typography
            variant="caption"
            sx={{
              display: "block",
              mt: 0.5,
              color: errors[field.name]
                ? theme.customColors.status.error.text
                : theme.customColors.text.secondary,
            }}
          >
            {errors[field.name] || values[field.name] || field.helperText}
          </Typography>
        </Box>
      );
    }

    // A list of short values: pick suggestions (`options`, plain strings)
    // or type new ones. Enter or leaving the field adds what was typed.
    if (field.type === "tags") {
      const current = values[field.name] || [];
      return (
        <Autocomplete
          key={field.name}
          multiple
          freeSolo
          autoSelect
          // Picked places leave the list, so choosing one again cannot
          // unselect it; a typed repeat is dropped below.
          filterSelectedOptions
          options={field.options || []}
          value={current}
          onChange={(e, next) => {
            const seen = new Set();
            const clean = next
              .map((v) => String(v).trim())
              .filter((v) => {
                const key = v.toLowerCase();
                if (!v || seen.has(key)) return false;
                seen.add(key);
                return true;
              });
            setValue(field.name, field.max ? clean.slice(0, field.max) : clean);
          }}
          renderTags={(selected, getTagProps) =>
            selected.map((value, index) => (
              <Chip
                {...getTagProps({ index })}
                key={value}
                size="small"
                label={value}
              />
            ))
          }
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              error={!!errors[field.name]}
              helperText={errors[field.name] || field.helperText}
            />
          )}
        />
      );
    }

    // One short value: type anything, or pick from `options` (strings).
    if (field.type === "suggest") {
      return (
        <Autocomplete
          key={field.name}
          freeSolo
          options={field.options || []}
          value={values[field.name] || ""}
          inputValue={values[field.name] || ""}
          onInputChange={(e, next) => setValue(field.name, next)}
          onChange={(e, next) => setValue(field.name, next || "")}
          renderInput={(params) => (
            <TextField
              {...params}
              label={field.label}
              error={!!errors[field.name]}
              helperText={errors[field.name] || field.helperText}
              inputProps={{ ...params.inputProps, maxLength: field.maxLength }}
            />
          )}
        />
      );
    }

    const isMulti = field.type === "multiselect";
    const optionLabel = (value) =>
      field.options?.find((o) => o.value === value)?.label ?? value;

    return (
      <TextField
        key={field.name}
        fullWidth
        select={field.type === "select" || isMulti}
        multiline={field.type === "textarea"}
        minRows={field.type === "textarea" ? field.minRows || 3 : undefined}
        type={field.type === "date" ? "date" : undefined}
        InputLabelProps={field.type === "date" ? { shrink: true } : undefined}
        SelectProps={
          isMulti
            ? {
                multiple: true,
                renderValue: (selected) => (
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        size="small"
                        label={optionLabel(value)}
                      />
                    ))}
                  </Box>
                ),
              }
            : undefined
        }
        label={field.label}
        value={values[field.name] ?? (isMulti ? [] : "")}
        onChange={(e) => setValue(field.name, e.target.value)}
        error={!!errors[field.name]}
        helperText={errors[field.name] || field.helperText}
      >
        {(field.type === "select" || isMulti) &&
          field.options.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {isMulti && (
                <Checkbox
                  size="small"
                  checked={(values[field.name] || []).includes(option.value)}
                  sx={{ p: 0, mr: 1 }}
                />
              )}
              {option.label}
            </MenuItem>
          ))}
      </TextField>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={dialogPaperProps}
    >
      <DialogHeader title={title} onClose={onClose} />
      <DialogContent dividers>
        <Stack spacing={2} sx={{ pt: 1 }}>
          {visibleFields.map(renderField)}
        </Stack>

        {isEdit && onRemove && removeBlockedReason && (
          <Typography
            variant="body2"
            sx={{
              mt: 3,
              pt: 2,
              fontStyle: "italic",
              color: theme.customColors.text.secondary,
              borderTop: `1px solid ${theme.customColors.border.main}`,
            }}
          >
            {removeBlockedReason}
          </Typography>
        )}
        {isEdit && onRemove && !removeBlockedReason && (
          <Box
            sx={{
              mt: 3,
              pt: 2,
              borderTop: `1px solid ${theme.customColors.border.main}`,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                color: theme.customColors.text.secondary,
                mb: 1,
              }}
            >
              {confirmRemove
                ? "Click again to remove it. This cannot be undone."
                : removeHelperText}
            </Typography>
            <Button
              variant="outlined"
              color="error"
              onClick={() => {
                if (!confirmRemove) {
                  setConfirmRemove(true);
                  return;
                }
                onRemove(initialValues.id);
                onClose();
              }}
              sx={{ borderRadius: "8px", textTransform: "none" }}
            >
              {confirmRemove ? "Yes, remove" : "Remove"}
            </Button>
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={dialogActionsSx(theme)}>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={outlinedButtonSx(theme)}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={uploading}
          sx={primaryButtonSx(theme)}
        >
          {isEdit ? "Save changes" : "Add"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ItemDialog;
